// Utility helpers extracted from InformationManager component logic.
// These helpers focus on data preparation to keep the component lean.

/**
 * Normalizes request payloads before sending them to the API.
 * @param {Array<Object>} requests - Raw request objects from the component.
 * @param {Object} options - Additional context for the transformation.
 * @param {string} options.projectId - Active project identifier.
 * @param {string} options.cycleId - Active cycle identifier.
 * @param {boolean} options.isFreePlan - Whether the customer is on the free plan.
 * @returns {Array<Object>} Normalized request array.
 */
export function prepareRequestsForUpdate(requests = [], { projectId = '', cycleId = '', isFreePlan = false } = {}) {
  if (!Array.isArray(requests)) return [];

  return requests.map((request) => {
    const normalized = { ...(request || {}) };
    normalized.project = projectId;
    if (cycleId) normalized.cycleId = cycleId;

    if (isFreePlan) {
      normalized.type = 'text';
      return normalized;
    }

    if (normalized.type === 'text' && normalized.uploadlink) {
      normalized.type = normalized.uploadlink;
    }

    return normalized;
  });
}

/**
 * Builds a summary hash for information owners, aggregating counts and metadata.
 * @param {Array<Object>} requests - Information manager requests.
 * @returns {Object} Summary keyed by info owner id.
 */
export function buildInfoOwnerSummary(requests = []) {
  if (!Array.isArray(requests)) return {};

  return requests.reduce((summary, request) => {
    const infoOwnerId = request?.info_owner;
    if (!infoOwnerId) return summary;

    const current = summary[infoOwnerId] || {
      lastmail: (request?.info_owner_detail?.mails || {}).action_date,
      _id: infoOwnerId,
      name: request?.info_owner_detail?.name,
      email: request?.info_owner_detail?.email,
      pending: 0,
      approve: 0,
      close: 0,
      cancel: 0,
      mailsent: 0,
      mailpending: 0,
    };

    const mailCount = request?.mail_count || 0;
    if (mailCount > 0) current.mailsent += 1;
    else current.mailpending += 1;

    if (request?.stage === -1) current.cancel += 1;
    else if (request?.stage === 0) current.pending += 1;
    else if (request?.stage === 2) current.close += 1;
    else if (request?.stage === 1) current.approve += 1;

    summary[infoOwnerId] = current;
    return summary;
  }, {});
}

/**
 * Returns an empty bulk upload CSV template rows list.
 * @returns {Array<Array<string>>} Template rows for bulk upload.
 */
export function buildBulkUploadTemplateRows() {
  return [
    ['description', 'category', 'info_owner_name', 'info_owner_email', 'file_upload', 'uploadlink', 'priority', 'duedate', 'location'],
  ];
}

/**
 * Prepares rows required for exporting the request list as CSV.
 * @param {Object} options - Export configuration.
 * @param {string} options.projectName - Name of the active project.
 * @param {string|Date} options.projectDueDate - Project due date.
 * @param {Array<Object>} options.requests - Requests to export.
 * @param {Function} options.getLocationName - Resolver for location id to name.
 * @param {Function} options.getStageLabel - Resolver for stage number to label.
 * @param {Function} options.formatDate - Date formatter utility.
 * @returns {Array<Array<any>>} CSV rows.
 */
export function buildRequestExportRows({
  projectName = '',
  projectDueDate = '',
  requests = [],
  getLocationName = () => '',
  getStageLabel = () => '',
  formatDate = (value) => value,
} = {}) {
  const rows = [[]];
  rows.push(['', 'Project Name', projectName]);
  rows.push(['', 'Due Date', projectDueDate]);
  rows.push([]);
  rows.push([['', 'Sl No', 'Description', 'Category', 'Location', 'Type', 'Due Date', 'Information Owner Name', 'Information Owner Email', 'Status', 'Response', 'Remarks']]);

  requests.forEach((request, index) => {
    const infoOwnerDetail = request?.info_owner_detail || {};
    const typeLabel = request?.type === 'text'
      ? 'Text'
      : (request?.type === 'fileuploadtolink'
        ? 'File Upload to Link'
        : (request?.type === 'fileupload' ? 'File Upload' : 'Unknown'));

    let responseLabel = request?.response || '';
    if (request?.type === 'fileuploadtolink' && request?.response) {
      responseLabel = 'Uploaded to Link';
    } else if (request?.type === 'fileupload' && request?.response) {
      responseLabel = 'Uploaded';
    }

    const row = [
      '',
      index + 1,
      `"${request?.description || ''}"`,
      `"${request?.category || ''}"`,
      `"${getLocationName(request?.location) || ''}"`,
      typeLabel,
      formatDate(request?.duedate || 'NA'),
      infoOwnerDetail.name,
      infoOwnerDetail.email,
      getStageLabel(request?.stage),
      responseLabel,
      `"${request?.remark || ''}"`,
    ];

    rows.push(row);
  });

  return rows;
}

/**
 * Normalizes CSV rows into an object compatible with the bulk upload endpoint.
 * @param {Array<Array<string>>} rows - Parsed CSV data.
 * @param {Object} options - Transformation flags and utilities.
 * @param {Object} options.infoOwnerById - Information owner objects keyed by id.
 * @param {boolean} options.isFreePlan - Whether the project runs on the free plan.
 * @param {Function} options.isValidDate - Utility to validate dates in a given format.
 * @param {Function} options.formatDateISO - Utility to format dates into ISO string.
 * @returns {Promise<{ data: Array<Object>, error: Object }>} Normalized data and error map.
 */
export function validateBulkUploadRows(rows = [], {
  infoOwnerById = {},
  isFreePlan = false,
  isValidDate = () => false,
  formatDateISO = () => '',
} = {}) {
  return new Promise((resolve, reject) => {
    try {
      if (!Array.isArray(rows) || rows.length === 0) {
        resolve({ data: [], error: {} });
        return;
      }

      const header = rows[0] || [];
      const requiredHeader = 'description,category,info_owner_name,info_owner_email,file_upload,uploadlink,priority,duedate';
      if (header.join(',') !== requiredHeader) {
        reject('all required columns were not found!');
        return;
      }

      const emailToInfoOwnerId = Object.keys(infoOwnerById).reduce((acc, key) => {
        const owner = infoOwnerById[key];
        if (owner?.email) {
          acc[owner.email] = owner._id;
        }
        return acc;
      }, {});

      const normalizedData = [];
      const errorMap = {};

      for (let index = 1; index < rows.length; index += 1) {
        const row = rows[index];
        if (!Array.isArray(row) || row.length === 0) {
          continue;
        }

        if (row.length !== header.length) {
          if (row.length > 1) {
            const invalidRow = { ...row, status: -1 };
            normalizedData.push(invalidRow);
            errorMap[`index${normalizedData.length}`] = ['Incomplete data'];
          }
          continue;
        }

        const validationErrors = [];
        const record = {};

        if (row[0]) record.description = row[0];
        else validationErrors.push('Description is required');

        if (row[1]) record.category = row[1];

        if (!row[3]) {
          validationErrors.push('info_owner_email is required');
        } else if (emailToInfoOwnerId[row[3]]) {
          record.info_owner = emailToInfoOwnerId[row[3]];
          record.info_owner_email = row[3];
          record.info_owner_name = row[2];
        } else if (!row[2]) {
          validationErrors.push('info_owner_name is required');
        } else {
          record.info_owner_email = row[3];
          record.info_owner_name = row[2];
        }

        const uploadFlag = (row[4] || '').toLowerCase();
        record.uploadlink = row[5] || '';
        if (uploadFlag === 'y' || uploadFlag === 'yes') {
          record.fileupload = true;
          if (isFreePlan && !record.uploadlink) {
            validationErrors.push('uploadlink is required');
          }
        } else {
          record.fileupload = false;
        }

        const urgentFlag = (row[6] || '').toLowerCase();
        record.urgent = urgentFlag === 'y' || urgentFlag === 'yes';

        if (row[7]) {
          if (isValidDate(row[7], 'DD-MM-YYYY')) {
            record.duedate = formatDateISO(row[7], 'DD-MM-YYYY');
          } else {
            validationErrors.push('Invalid duedate');
          }
        }

        if (validationErrors.length > 0) {
          errorMap[`index${normalizedData.length}`] = validationErrors;
          record.status = false;
        } else {
          record.status = true;
        }

        normalizedData.push(record);
      }

      resolve({ data: normalizedData, error: errorMap });
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Converts CSV rows into a data URI string that can be used for downloads.
 * @param {Array<Array<any>>} rows - Matrix of CSV values.
 * @returns {string} CSV data URI string.
 */
export function buildCsvDataUri(rows = []) {
  const csvLines = rows.map((row) => {
    if (!Array.isArray(row)) return '';
    return row.map((value) => (value === null || value === undefined ? '' : value)).join(',');
  });
  return `data:text/csv;charset=utf-8,${csvLines.join('\n')}`;
}

/**
 * Collects attachment entries from legacy request payloads.
 * @param {Object} source - Request or header object containing attachment fields.
 * @returns {Array<Object>} Normalised attachment objects.
 */
export function collectAttachmentEntries(source = {}) {
  if (!source || typeof source !== 'object') return [];

  const attachments = [];
  const candidates = [source.fileid_detail, source.fileid_details, source.file_details, source.upload_v2];

  candidates.forEach((entry) => {
    if (Array.isArray(entry)) {
      attachments.push(...entry.filter(Boolean));
    } else if (entry && typeof entry === 'object') {
      attachments.push(entry);
    }
  });

  if (attachments.length === 0 && Array.isArray(source.fileid)) {
    source.fileid.filter(Boolean).forEach((id, index) => {
      attachments.push({
        _id: id,
        fileid: id,
        originalname: `Document ${index + 1}`,
      });
    });
  }

  return attachments;
}

/**
 * Resolves attachment identifiers following a preferred field order.
 * @param {Object} file - Attachment object.
 * @param {Array<string>} fieldOrder - Preferred identifier fields.
 * @returns {Array<string|number>} Normalised list of attachment ids.
 */
export function resolveAttachmentIds(file, fieldOrder = ['_id', 'fileid', 'file_id', 'id', 'uploadid', 'upload_id']) {
  if (!file || typeof file !== 'object') return [];

  for (const field of fieldOrder) {
    const value = file[field];
    if (!value) continue;

    if (Array.isArray(value)) {
      const filtered = value.filter(Boolean);
      if (filtered.length > 0) return filtered;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return [value];
    }
  }

  return [];
}

/**
 * Produces a stable key for rendering attachment items.
 * @param {Object} file - Attachment object.
 * @param {number} index - Fallback index.
 * @returns {string|number} Stable key.
 */
export function resolveAttachmentKey(file, index) {
  if (!file || typeof file !== 'object') return `attachment-${index}`;

  const keyFields = ['_id', 'fileid', 'file_id', 'id'];
  for (const field of keyFields) {
    if (file[field]) return file[field];
  }

  return `attachment-${index}`;
}
